/** @file updatePlants: the next plant map after this frame's step/hook events, with follow points refreshed. */
import type { ClipEvent } from '@zilo/pose3d-format/types/clip';
import type { Vec3 } from '@zilo/pose3d-math/vec3/vec3';
import type { PoseBuffer, PoseParams } from '@zilo/pose3d-pose/contract';
import { eventWorldPoint } from '@zilo/pose3d-pose/events/eventWorldPoint';
import type { FiredEvent, Plant, Plants } from '@zilo/pose3d-pose/events/eventTypes';
import type { Rig } from '@zilo/pose3d-pose/rig/rigTypes';

const PLANTING: ReadonlySet<ClipEvent['type']> = new Set(['step', 'hook1', 'hook2', 'hook3']);

/**
 * A new map (the input is never mutated): for each fired `step`/`hook1..3` event, `fixed` (the default) captures the
 * current world point, `follow` starts tracking it, `stop` removes the node's plant; then every `follow` plant's point
 * is refreshed from `pose` (decision 21). Other event types are ignored.
 */
export function updatePlants(plants: Plants, fired: FiredEvent[], pose: PoseBuffer, rig: Rig, params: PoseParams, entityPos: Vec3): Plants {
  const next = new Map(plants);
  for (const { event } of fired) {
    if (!PLANTING.has(event.type)) continue;
    const node = event.node ?? rig.names[0] ?? '';
    const mode = event.mode ?? 'fixed';
    if (mode === 'stop') {
      next.delete(node);
      continue;
    }
    const plant: Plant = { mode, type: event.type, point: eventWorldPoint(pose, rig, node, event.pos, params, entityPos) };
    next.set(node, event.pos === undefined ? plant : { ...plant, offset: event.pos });
  }
  for (const [node, plant] of next) {
    if (plant.mode === 'follow') next.set(node, { ...plant, point: eventWorldPoint(pose, rig, node, plant.offset, params, entityPos) });
  }
  return next;
}
